import React, { useState } from "react";
import axios from "axios";
import {
  Modal,
  Upload,
  Button,
  message,
  Spin,
  Alert,
} from "antd";
import { InboxOutlined, CameraOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const { Dragger } = Upload;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
  }
`;

const UploadArea = styled.div`
  .ant-upload.ant-upload-drag {
    border: 2px dashed #d9d9d9;
    border-radius: 6px;
    background-color: #fafafa;
    
    &:hover {
      border-color: #3c2f61;
    }
  }
`;

const CurrentImage = styled.div`
  margin-bottom: 20px;
  text-align: center;
`;

const CurrentImageTitle = styled.h4`
  color: #3c2f61;
  margin-bottom: 10px;
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 80px;
  font-weight: bold;
`;

const UpdateImageModal = ({ visible, event, onCancel, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileChange = (info) => {
    if (info.file.status === "removed") {
      setImageFile(null);
      setPreviewUrl(null);
      return;
    }

    const file = info.file.originFileObj || info.file;
    const isImage = file.type.startsWith("image/");
    const isValidFormat = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
    const isLt2M = file.size / 1024 / 1024 < 2;

    if (!isImage || !isValidFormat) {
      message.error("You can only upload JPG/PNG/WEBP files!");
      return;
    }

    if (!isLt2M) {
      message.error("Image must be smaller than 2MB!");
      return;
    }

    setImageFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!imageFile) {
      message.warning("Please select an image to upload");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("image", imageFile);

      const response = await axios.put(
        `${BASE_URL}/companies/events/${event.id}/update-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 && response.data.status === "success") {
        message.success("Event image updated successfully!");
        setImageFile(null);
        setPreviewUrl(null);
        onSuccess();
      } else {
        message.error(response.data.message || "Failed to update image");
      }
    } catch (error) {
      console.error("Error updating image:", error);
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error("Failed to update image. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setImageFile(null);
    setPreviewUrl(null);
    onCancel();
  };

  const uploadProps = {
    accept: "image/jpeg,image/png,image/webp",
    multiple: false,
    maxCount: 1,
    beforeUpload: () => false,
    onChange: handleFileChange,
    onRemove: () => {
      setImageFile(null);
      setPreviewUrl(null);
    },
  };

  return (
    <StyledModal
      title="Update Event Image"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <CurrentImage>
          <CurrentImageTitle>Current Image</CurrentImageTitle>
          {event.image_url ? (
            <ImagePreview src={event.image_url} alt={event.name} />
          ) : (
            <ImagePlaceholder>
              {event.name.charAt(0).toUpperCase()}
            </ImagePlaceholder>
          )}
        </CurrentImage>

        <Alert
          message="Image Guidelines"
          description="Upload a new image in JPG, PNG, or WEBP format. Maximum size: 2MB. The new image will replace the current one."
          type="info"
          showIcon
          style={{ marginBottom: "20px" }}
        />

        {previewUrl && (
          <CurrentImage>
            <CurrentImageTitle>New Image Preview</CurrentImageTitle>
            <ImagePreview src={previewUrl} alt="New image preview" />
          </CurrentImage>
        )}

        <UploadArea>
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag image to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for JPG, PNG, or WEBP. Maximum size: 2MB
            </p>
          </Dragger>
        </UploadArea>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<CameraOutlined />}
            onClick={handleSubmit}
            loading={loading}
            disabled={!imageFile}
            style={{ backgroundColor: "#3c2f61", borderColor: "#3c2f61" }}
          >
            Update Image
          </Button>
        </div>
      </Spin>
    </StyledModal>
  );
};

export default UpdateImageModal;